import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { BrandsService } from 'src/app/services/brands/brands.service';
import {
  OneTimeRewardsRequest,
  OneTimeRewardsService,
} from 'src/app/services/one-time-rewards/one-time-rewards.service';
import { SpinnerService } from '../../../services/spinner/spinner.service';
import { TeamsService } from '../../../services/teams/teams.service';

@Component({
  selector: 'app-member-reward-dialog',
  templateUrl: './memberrewarddialog.component.html',
  styleUrls: ['./memberrewarddialog.component.scss'],
})
export class MemberRewardDialogComponent implements OnInit {
  public team: any;
  public teamMember;
  public form;
  public isP2p: boolean;
  public title = '';
  public sending = false;
  public defaultPaymentMethod: any;

  serviceFee = 0;

  constructor(
    private route: ActivatedRoute,
    private teamsService: TeamsService,
    private formBuilder: UntypedFormBuilder,
    public dialogRef: MatDialogRef<MemberRewardDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MemberRewardDialogModel,
    private spinnerService: SpinnerService,
    private readonly brandsService: BrandsService,
    private readonly oneTimeRewardsService: OneTimeRewardsService,
  ) {
    this.teamMember = data.member;
    this.isP2p = data.isP2p;
    this.team = data.team;
    this.title = this.isP2p ? 'Top Up Peer Recognition Balance' : 'Send One Time Payment to Member';
    this.form = this.formBuilder.group({
      amount: new UntypedFormControl('', Validators.required),
      message: new UntypedFormControl('', Validators.required),
      note: new UntypedFormControl(''),
      destination: new UntypedFormControl(false, Validators.required),
      wallitBalance: new UntypedFormControl(false, [Validators.required]),
    });
  }

  async ngOnInit(): Promise<void> {
    this.teamsService.wellspaceChangedObservable.subscribe((wellspace) => {
      if (!this.team) {
        this.team = wellspace;
      }
    });
    if (this.canUseWallitBalance()) {
      this.spinnerService.show();
      if (this.team.defaultPaymentMethod) {
        const teamPaymentMethod = await this.teamsService.getPaymentMethod(this.team);
        const teamPaymentMethods = await this.teamsService.getPaymentMethods(this.team.id);
        this.defaultPaymentMethod = teamPaymentMethods.find(
          (thisMethod: { stripePaymentMethod: string }) =>
            thisMethod.stripePaymentMethod === teamPaymentMethod,
        );
      } else {
        this.form.get('wallitBalance')?.setValue(true);
      }
      this.spinnerService.hide();
    }
  }

  private getFormField(name: string): any {
    const field = this.form.get(name);
    return field ? field.value : undefined;
  }

  async sendClicked(): Promise<void> {
    const amount = this.getFormField('amount');
    this.sending = true;

    const oneTimeRewardsRequest: OneTimeRewardsRequest = {
      amount: amount,
      message: this.getFormField('message'),
      note: this.getFormField('note'),
      destination: this.getFormField('destination'),
      recipients: [
        {
          disabled: this.teamMember.disabled,
          email: this.teamMember.email || this.teamMember.userEmail,
          inviteSentAt: this.teamMember.inviteSentAt,
          message: this.teamMember.message,
          metadata: this.teamMember.metadata,
          removalStatus: this.teamMember.removalStatus,
          status: this.teamMember.status,
          teamUserId: this.teamMember.memberId || this.teamMember.teamUserId || this.teamMember.id,
          userId: this.teamMember.userId,
        },
      ],
      isP2p: this.isP2p,
      team: this.team,
      toTeamId: this.team.id,
      isUsingWallitBalance: this.getFormField('wallitBalance'),
    };

    const success = await this.oneTimeRewardsService.processOneTimeReward(oneTimeRewardsRequest);
    this.dialogRef.close(success ? amount : 0);
  }

  canUseWallitBalance() {
    const brandConfig = this.brandsService.getConfigJSON();
    // TODO: When getConfigJSON is fixed, remove comment
    const brandHaveAccounts = true;// brandConfig.content?.ui?.tabs?.accounts;
    const teamMemberUserId = this.teamMember?.userId;
    return !this.isP2p && brandHaveAccounts && !!teamMemberUserId && teamMemberUserId !== this.team.ownerUserId;
  }

  canUseWallitDestination() {
    return !!this.teamMember?.userId;
  }

  onDismiss(): void {
    this.dialogRef.close(0);
  }
}

export class MemberRewardDialogModel {
  constructor(public member: any, public isP2p: boolean, public team?: any) {}
}
